import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useAdminUsersList, type AdminUser } from "@/queries/adminUsers";
import { useMe } from "@/queries/auth";

import { AdminUsersTable } from "./AdminUsersTable";
import { CreateUserDialog } from "./CreateUserDialog";
import { DeactivateUserDialog } from "./DeactivateUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

type RoleFilter = "all" | "admin" | "user";
type StatusFilter = "all" | "active" | "deactivated";

interface AdminUsersPageProps {
  /** When true, the create dialog is open on mount (rendered by /admin/users/new). */
  openCreate?: boolean;
}

export function AdminUsersPage({ openCreate = false }: AdminUsersPageProps) {
  const { t } = useTranslation("admin-users");
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data, isPending, isError } = useAdminUsersList();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (statusFilter === "active" && !u.is_active) return false;
      if (statusFilter === "deactivated" && u.is_active) return false;
      if (needle === "") return true;
      return (
        u.email.toLowerCase().includes(needle) ||
        (u.display_name?.toLowerCase().includes(needle) ?? false)
      );
    });
  }, [data, search, roleFilter, statusFilter]);

  const handleCreateOpenChange = (next: boolean) => {
    if (!next) void navigate({ to: "/admin/users" });
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("list.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("list.subtitle")}</p>
        </div>
        <Button
          onClick={() => void navigate({ to: "/admin/users/new" })}
        >
          <Plus className="h-4 w-4" />
          {t("list.newUser")}
        </Button>
      </header>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          aria-label={t("list.searchPlaceholder")}
          placeholder={t("list.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-sm"
        />
        <Select
          aria-label={t("list.filters.role")}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
          className="sm:max-w-[180px]"
        >
          <option value="all">{t("list.filters.roleAll")}</option>
          <option value="admin">{t("list.filters.roleAdmin")}</option>
          <option value="user">{t("list.filters.roleUser")}</option>
        </Select>
        <Select
          aria-label={t("list.filters.status")}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="sm:max-w-[180px]"
        >
          <option value="all">{t("list.filters.statusAll")}</option>
          <option value="active">{t("list.filters.active")}</option>
          <option value="deactivated">{t("list.filters.deactivated")}</option>
        </Select>
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">{t("list.loading")}</p>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertDescription>{t("list.loadError")}</AlertDescription>
        </Alert>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("list.empty")}</p>
      ) : (
        <AdminUsersTable
          users={filtered}
          selfId={me?.id ?? ""}
          onResetPassword={setResetTarget}
          onDeactivate={setDeactivateTarget}
          onDelete={setDeleteTarget}
        />
      )}

      <CreateUserDialog open={openCreate} onOpenChange={handleCreateOpenChange} />

      {resetTarget ? (
        <ResetPasswordDialog
          open
          onOpenChange={(next) => !next && setResetTarget(null)}
          user={resetTarget}
        />
      ) : null}

      {deactivateTarget ? (
        <DeactivateUserDialog
          open
          onOpenChange={(next) => !next && setDeactivateTarget(null)}
          user={deactivateTarget}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteUserDialog
          open
          onOpenChange={(next) => !next && setDeleteTarget(null)}
          user={deleteTarget}
        />
      ) : null}
    </div>
  );
}
